/**
 * @ComponentFor CmsPageViewModel
 */
import React from 'react';
import { styled } from '@glitz/react';
import { EpiProperty, epiPropertyValue } from '@polarnopyret/scope-episerver';
import Page from 'Shared/Page';
import CmsPageViewModelType from './CmsPageViewModel.type';
import Viewport from 'Shared/Viewport';
import { HEIGHT as DESKTOP_HEADER_HEIGHT } from 'SiteLayout/Header/DesktopHeader';
import { BLACK_TOP_MOBILE_HEIGHT, BLACK_TOP_DESKTOP_HEIGHT } from 'SiteLayout/Header';
import { HEIGHT as MOBILE_HEIGHT } from 'SiteLayout/Header/MobileHeader';
import { QUICKSEARCH_HEIGHT } from 'Search/QuickSearch';
import { setImageBackground, Preset } from 'Shared/image-background';
import Breadcrumbs from 'Shared/SharedComponents/atoms/Breadcrumbs';
import { H1, Paragraph, ResponsiveSize, TextLabel } from 'Shared/SharedComponents/atoms/Typography';
import { GridWrap } from 'Shared/PageLayout';
import { createGridClasses, createOffsetGridClassLarge } from 'Shared/grid';
import { selectDeviceImageFromProperty } from 'Shared/utils';
import * as style from 'Shared/Style';

type PropType = CmsPageViewModelType;

const PageWrap = styled.div({
  overflow: 'hidden',
  background: {
    size: 'cover',
    positionX: 'center',
    positionY: 'center',
  },
  height: '100vh',
})

const ImageContent = styled(GridWrap, {
  height: '100%',
  alignItems: 'center',
  ['@media ' + style.mediaUpToLarge]: {
    position: 'relative'
  },
})

const IntroWrap = styled.div({
  margin: {
    y: '40px',
  },
  ['@media ' + style.mediaMinLarge]: {
    margin: {
      y: '80px',
    },
  },
})

const CenterBlock = styled.div({
  textAlign: 'center',
})

export default function CmsPage(props: PropType) {
  const topMarginCompact = QUICKSEARCH_HEIGHT + MOBILE_HEIGHT + BLACK_TOP_MOBILE_HEIGHT;
  const topMarginDesktop = DESKTOP_HEADER_HEIGHT + BLACK_TOP_DESKTOP_HEIGHT;
  const introText = epiPropertyValue(props.page.mainIntro);

  return (
    <Viewport>
      {(isCompact) => (
        <Page>
          <PageWrap
            css={{
              marginTop: isCompact ? -topMarginCompact : -topMarginDesktop,
              background: {
                image: setImageBackground(selectDeviceImageFromProperty(props.page.image, props.page.imageSmallScreen, isCompact)?.url, Preset.FullWidth, 'linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))'),
              },
              //background: linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)), url(image.png);
            }}
          >
            <ImageContent className='grid'>
              <styled.Div className={createGridClasses(12, 1)} css={{ ...(isCompact && { position: 'absolute', top: `${topMarginCompact + 12}px` }) }}>
                <Breadcrumbs breadcrumbs={props.breadcrumbs} vertical={!isCompact} white />
              </styled.Div>
              <div className={createGridClasses(12, 10, createOffsetGridClassLarge(2) + (!isCompact ? ' grid subgrid-10' : ''))}>
                <CenterBlock className={createGridClasses(12, 4, createOffsetGridClassLarge(4))}>
                  <TextLabel fontSize={ResponsiveSize.D16_M16} css={{ marginBottom: '16px', color: style.colors.monochrome.white }}>
                    {epiPropertyValue(props.page.label)}
                  </TextLabel>
                </CenterBlock>
                <CenterBlock className={createGridClasses(12, 8, createOffsetGridClassLarge(2))}>
                  <H1 css={{ color: style.colors.monochrome.white }} fontSize={ResponsiveSize.D96_M40}>{epiPropertyValue(props.page.heading)}</H1>
                </CenterBlock>
              </div>
            </ImageContent>
          </PageWrap>
          {introText && (
            <GridWrap className='grid'>
              <IntroWrap className={createGridClasses(12, 8, createOffsetGridClassLarge(2))} >
                <Paragraph fontSize={ResponsiveSize.D32_M24}>{introText}</Paragraph>
              </IntroWrap>
            </GridWrap>
          )}
          <EpiProperty for={props.page.mainContentArea} />
        </Page>
      )
      }
    </Viewport >
  );
}
